import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Grid,
  Icon,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsAppNumberPage() {
  const pageTitle = "WhatsApp Number";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [number, setNumber] = useState(null);
  const [agents, setAgents] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");

  const [insights, setInsights] = useState({
    contacts: 0,
    conversations: 0,
    open_conversations: 0,
    closed_conversations: 0,
    messages: 0,
    incoming_messages: 0,
    outgoing_messages: 0,
    sent_messages: 0,
    delivered_messages: 0,
    read_messages: 0,
  });

  const [agentDialogOpen, setAgentDialogOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      try {
        const numberResult = await axios.get(`/portal/wacc/businesses/numbers/${ref}/fetch.json`);

        if (numberResult.data.status !== "success") {
          setShowLoading(false);

          await statusDialog.show(numberResult.data.data.message, "error", "Request Failed");

          return;
        }

        setNumber(numberResult.data.data.number);
        setAgents(numberResult.data.data.agents || []);
        setAssignedAgents(numberResult.data.data.assigned_agents || []);

        const insightsResult = await axios.get(
          `/portal/wacc/businesses/numbers/${ref}/insights.json`
        );

        if (insightsResult.data.status === "success") {
          setInsights(insightsResult.data.data);
        } else {
          setErrorMsg(insightsResult.data.data.message);
          setSeverity("error");
          setOpen(true);
        }

        setPageLoaded(true);
        setShowLoading(false);
      } catch (error) {
        setShowLoading(false);

        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`,
          "error",
          "Request Failed"
        );
      }
    };

    fetchData();
  }, [ref]);

  const handleAddAgent = async () => {
    if (!selectedAgent) {
      return;
    }

    setAgentDialogOpen(false);
    setShowLoading(true);

    await axios
      .post("/portal/wacc/businesses/numbers/agents/add.json", {
        number: ref,
        agent: selectedAgent,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          const agent = result.data.data.agent;
          setAssignedAgents((current) => [...current, agent]);
          setAgents((current) => current.filter((item) => item.row !== selectedAgent));
          setSelectedAgent("");
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const handleRemoveAgent = async (agent) => {
    setShowLoading(true);

    await axios
      .post("/portal/wacc/businesses/numbers/agents/remove.json", {
        reference: agent.reference,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setAssignedAgents((current) =>
            current.filter((item) => item.reference !== agent.reference)
          );

          setAgents((current) =>
            [...current, result.data.data.agent].sort((a, b) => a.name.localeCompare(b.name))
          );
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const handleAgentStatus = async (agent) => {
    setShowLoading(true);

    await axios
      .post("/portal/wacc/businesses/numbers/agents/status.json", {
        reference: agent.reference,
        status: !agent.status,
      })
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setAssignedAgents((current) =>
            current.map((item) =>
              item.reference === agent.reference
                ? {
                    ...item,
                    status: result.data.data.agent.status,
                  }
                : item
            )
          );
        } else {
          setErrorMsg(result.data.data.message);
          setSeverity("error");
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);

        setErrorMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setSeverity("error");
        setOpen(true);
      });
  };

  const insightCard = (title, value, icon) => (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card>
        <MDBox p={2} display="flex" alignItems="center">
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="3rem"
            height="3rem"
            borderRadius="lg"
            sx={{
              backgroundColor: "#f5f5f5",
            }}
          >
            <Icon>{icon}</Icon>
          </MDBox>

          <MDBox ml={2}>
            <MDTypography variant="text" color="text" fontSize={12}>
              {title}
            </MDTypography>

            <MDTypography variant="h6" fontWeight="bold">
              {value}
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </Grid>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={open} autoHideDuration={6000} onClose={() => setOpen(false)}>
        <Alert onClose={() => setOpen(false)} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={agentDialogOpen}
        onClose={() => setAgentDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Add Agent</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <Autocomplete
              fullWidth
              options={agents}
              value={agents.find((agent) => agent.row === selectedAgent) || null}
              onChange={(event, value) => {
                setSelectedAgent(value ? value.row : "");
              }}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.row === value.row}
              autoHighlight
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Agent"
                  placeholder="Search agent..."
                  size="small"
                />
              )}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <Button color="dark" size="small" onClick={() => setAgentDialogOpen(false)}>
            Cancel
          </Button>

          <MDButton
            variant="contained"
            color="dark"
            size="small"
            disabled={!selectedAgent}
            onClick={handleAddAgent}
          >
            Add Agent
          </MDButton>
        </DialogActions>
      </Dialog>

      {pageLoaded && number && (
        <MDBox py={3}>
          <MDBox mb={2} display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/wacc`);
              }}
            >
              Back
            </Button>

            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => navigate(`/wacc/businesses/number/${ref}/contacts`)}
                startIcon={<Icon>people</Icon>}
                size="small"
              >
                Contacts
              </MDButton>

              <MDButton
                variant="outlined"
                color="dark"
                onClick={() => navigate(`/wacc/businesses/number/${ref}/conversations`)}
                startIcon={<Icon>forum</Icon>}
                size="small"
              >
                Conversations
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox mb={3}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="medium">
                      {number.title}
                    </MDTypography>

                    <MDTypography variant="button" color="text">
                      {number.phone_number}
                    </MDTypography>

                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      WhatsApp ID: {number.wa_reference}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>

          <MDBox mb={3}>
            <MDBox mb={2}>
              <MDTypography variant="h6">Overview</MDTypography>
            </MDBox>

            <Grid container spacing={2} mb={3}>
              {insightCard("Contacts", insights.contacts, "people")}
              {insightCard("Conversations", insights.conversations, "forum")}
              {insightCard("Open Conversations", insights.open_conversations, "chat")}
              {insightCard(
                "Closed Conversations",
                insights.closed_conversations,
                "chat_bubble_outline"
              )}
            </Grid>

            <MDBox mb={2}>
              <MDTypography variant="h6">Messages</MDTypography>
            </MDBox>

            <Grid container spacing={2}>
              {insightCard("Messages", insights.messages, "message")}
              {insightCard("Incoming Messages", insights.incoming_messages, "south_west")}
              {insightCard("Outgoing Messages", insights.outgoing_messages, "north_east")}
              {insightCard("Sent", insights.sent_messages, "send")}
              {insightCard("Delivered", insights.delivered_messages, "done_all")}
              {insightCard("Read", insights.read_messages, "done_all")}
            </Grid>
          </MDBox>

          <MDBox mt={5} mb={3}>
            <Card>
              <MDBox p={2}>
                <MDBox mb={2}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <MDTypography variant="h6">Assigned Agents</MDTypography>

                    <MDButton
                      variant="contained"
                      color="dark"
                      size="small"
                      startIcon={<Icon>person_add</Icon>}
                      onClick={() => {
                        setSelectedAgent("");
                        setAgentDialogOpen(true);
                      }}
                      disabled={agents.length === 0}
                    >
                      Add Agent
                    </MDButton>
                  </MDBox>
                </MDBox>

                <Divider my={3} />

                {assignedAgents.length === 0 ? (
                  <MDTypography variant="button" color="text">
                    No agents assigned to this number.
                  </MDTypography>
                ) : (
                  assignedAgents.map((agent) => (
                    <MDBox
                      key={agent.reference}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={1.5}
                      py={1}
                      px={1}
                      sx={{
                        borderBottom: "1px solid #eee",
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="button">{agent.name}</MDTypography>

                        <MDTypography variant="caption" color={agent.status ? "success" : "text"}>
                          <Chip size="small" label={agent.status ? "Active" : "Disabled"} />
                        </MDTypography>
                      </MDBox>

                      <MDBox display="flex" gap={1}>
                        <Tooltip title={agent.status ? "Disable Agent" : "Enable Agent"}>
                          <MDButton
                            variant="outlined"
                            color={agent.status ? "warning" : "success"}
                            size="small"
                            onClick={() => handleAgentStatus(agent)}
                            iconOnly
                          >
                            <Icon>{agent.status ? "person_off" : "person_add"}</Icon>
                          </MDButton>
                        </Tooltip>

                        <Tooltip title="Remove Agent">
                          <MDButton
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleRemoveAgent(agent)}
                            iconOnly
                          >
                            <Icon>person_remove</Icon>
                          </MDButton>
                        </Tooltip>
                      </MDBox>
                    </MDBox>
                  ))
                )}
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppNumberPage;
